import { adminClient, createApiClient } from './api-client';

/** Base path of the Vertex Identity API (the SQL-backed org system of record). */
const VERTEX_BASE = '/api/vertex/v1';

/** A client bound to the Vertex Identity API. */
function vertexClient(token: string) {
  return createApiClient(VERTEX_BASE, token);
}

// ─── Types ───

/** Sector classification */
export type OrgSector =
  | 'military' | 'law-enforcement' | 'fire-ems' | 'emergency-management'
  | 'medical' | 'environmental' | 'education' | 'transportation'
  | 'utilities' | 'private-security' | 'international-ngo';

/** Echelon level */
export type OrgEchelon = 'strategic' | 'operational' | 'tactical';

/** Node in the org tree */
export interface OrgTreeNode {
  id: string;
  name: string;
  shortName: string;
  type: string;
  echelon: OrgEchelon;
  children: OrgTreeNode[];
  plugins: string[];
  integrations: string[];
  mapView: { lat: number; lng: number; zoom: number };
}

/** Full org tree config */
export interface OrgTreeConfig {
  id: string;
  name: string;
  sector: OrgSector;
  branchOrType: string;
  units: OrgTreeNode[];
}

// ─── Sector Template Types ───

/** A sector template stored in Keycloak */
export interface StoredSectorTemplate {
  id: string;
  sector: OrgSector;
  typeName: string;
  suggestedPlugins: string[];
  tree: OrgTreeNode[];
  builtIn: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Top-level template store persisted in Keycloak realm attributes */
export interface SectorTemplateStore {
  version: number;
  seededAt: string;
  templates: StoredSectorTemplate[];
}

// ─── Realm Attribute Keys ───

/** Keycloak realm attribute keys for structured data storage */
const REALM_ATTRS = {
  orgTree: 'vertex.orgTree',
  sectorTemplates: 'vertex.sectorTemplates',
} as const;

/** Minimal Keycloak realm representation for attribute access */
interface RealmRepresentation {
  attributes: Record<string, string>;
  [key: string]: unknown;
}

// ─── Org Tree ───

/** Fetch the editable org config from the Vertex Identity API (SQL-backed). */
export async function fetchOrgTree(token: string): Promise<OrgTreeConfig | null> {
  const api = vertexClient(token);
  const { orgs } = await api.get<{ orgs: Array<{ id: string; name: string; slug: string }> }>('/orgs');
  const first = orgs[0];
  if (!first) return null;
  return api.get<OrgTreeConfig>(`/orgs/${first.id}/config`);
}

/** Save the org tree by reconciling it against the Vertex system of record. */
export async function saveOrgTree(token: string, tree: OrgTreeConfig): Promise<void> {
  await vertexClient(token).put(`/orgs/${tree.id}/tree`, tree);
}

// ─── Template Store ───

/** Fetch the sector template store from realm attributes */
export async function fetchTemplateStore(token: string): Promise<SectorTemplateStore | null> {
  const realm = await adminClient(token).get<RealmRepresentation>('');
  const raw = realm.attributes?.[REALM_ATTRS.sectorTemplates];
  if (!raw) return null;
  return JSON.parse(raw) as SectorTemplateStore;
}

/** Save the sector template store to realm attributes */
export async function saveTemplateStore(token: string, store: SectorTemplateStore): Promise<void> {
  await saveRealmAttribute(token, REALM_ATTRS.sectorTemplates, JSON.stringify(store));
}

// ─── Internal Helpers ───

/** Update a single realm attribute by reading current state and merging */
async function saveRealmAttribute(token: string, key: string, value: string): Promise<void> {
  const api = adminClient(token);
  const realm = await api.get<RealmRepresentation>('');
  await api.put('', {
    ...realm,
    attributes: { ...realm.attributes, [key]: value },
  });
}
